import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Calculator, Hash, TrendingUp, TrendingDown, Sigma, Percent } from 'lucide-react';
import { useDateTime } from '../contexts/DateTimeContext';
import { useTranslation } from 'react-i18next';

const AggregationDropdown = ({ value, onChange, columnKey, displayValue }) => {
  const { t } = useTranslation();
  const AGGREGATION_FUNCTIONS = [
    { key: 'none',  label: t('table_footer.agg_none'),    icon: null },
    { key: 'sum',   label: t('table_footer.agg_sum'),     icon: Sigma },
    { key: 'avg',   label: t('table_footer.agg_avg'),     icon: TrendingUp },
    { key: 'count', label: t('table_footer.agg_count'),   icon: Hash },
    { key: 'min',   label: t('table_footer.agg_min'),     icon: TrendingDown },
    { key: 'max',   label: t('table_footer.agg_max'),     icon: TrendingUp },
  ];
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => setIsOpen(false);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuStyle({ top: rect.top, left: rect.left });
    }
    setIsOpen(v => !v);
  };

  const currentFn = AGGREGATION_FUNCTIONS.find(f => f.key === value) || AGGREGATION_FUNCTIONS[0];
  const Icon = currentFn.icon;
  const hasValue = value !== 'none' && displayValue;

  return (
    <div ref={wrapperRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors text-xs"
      >
        {Icon ? <Icon className="w-3 h-3" /> : <Calculator className="w-3 h-3" />}
        {hasValue && <span className="text-foreground font-medium tabular-nums">{displayValue}</span>}
        <ChevronDown className="w-3 h-3" />
      </button>

      {isOpen && (
        <div
          className="fixed w-32 bg-popover border border-border rounded-lg shadow-xl z-[9999]"
          style={{ top: menuStyle.top, left: menuStyle.left, transform: 'translateY(-100%)' }}
        >
          {AGGREGATION_FUNCTIONS.map((fn) => {
            const FnIcon = fn.icon;
            return (
              <button
                key={fn.key}
                onClick={() => {
                  onChange(columnKey, fn.key);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                  value === fn.key
                    ? 'bg-accent text-foreground font-medium'
                    : 'text-foreground/80 hover:bg-accent hover:text-foreground'
                }`}
              >
                {FnIcon ? <FnIcon className="w-3 h-3" /> : <span className="w-3" />}
                <span>{fn.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Columns that have meaningful numeric/aggregatable data
const AGGREGATABLE_COLUMNS = new Set(['title', 'due_date', 'duration']);

const TableFooter = ({
  tasks,
  columns,
  visibleColumns,
  footerConfig,
  onFooterConfigChange,
  groupBy, // eslint-disable-line no-unused-vars
}) => {
  const { t, i18n } = useTranslation();
  const { formatDate } = useDateTime();
  // Calculate aggregations
  const aggregations = useMemo(() => {
    const results = {};

    columns.forEach((col) => {
      const fn = footerConfig[col.key]?.function || 'none';
      if (fn === 'none') {
        results[col.key] = null;
        return;
      }

      let values;
      switch (col.key) {
        case 'duration':
          values = tasks.map(t => t.duration || 0).filter(v => v > 0);
          break;
        case 'title':
          values = tasks.map(() => 1); // For count
          break;
        case 'due_date':
          values = tasks.map(t => t.due_date ? new Date(t.due_date).getTime() : null).filter(v => v !== null);
          break;
        case 'status':
        case 'priority':
          values = tasks.map(() => 1); // Only count makes sense
          break;
        default:
          values = [];
      }

      if (values.length === 0) {
        results[col.key] = null;
        return;
      }

      let result;
      switch (fn) {
        case 'sum':
          result = values.reduce((a, b) => a + b, 0);
          break;
        case 'avg':
          result = values.reduce((a, b) => a + b, 0) / values.length;
          break;
        case 'count':
          result = values.length;
          break;
        case 'min':
          result = Math.min(...values);
          break;
        case 'max':
          result = Math.max(...values);
          break;
        default:
          result = null;
      }

      results[col.key] = { value: result, function: fn };
    });

    return results;
  }, [tasks, columns, footerConfig]);

  // Format aggregation result for display
  const formatResult = (columnKey, result) => {
    if (!result) return null;

    const { value, function: fn } = result;

    switch (columnKey) {
      case 'duration': {
        if (fn === 'count') return t('table_footer.tasks_count', { count: value });
        const hours = Math.floor(value / 60);
        const mins = Math.round(value % 60);
        if (hours > 0) {
          return `${t('ui_common.units.hours_short', { value: hours })} ${t('ui_common.units.minutes_short', { value: mins })}`;
        }
        return t('ui_common.units.minutes_short', { value: mins });
      }

      case 'due_date':
        if (fn === 'count') return `${value}`;
        if (fn === 'min' || fn === 'max') {
          const date = new Date(value);
          return formatDate(date);
        }
        return `${value}`;

      case 'title':
      case 'status':
      case 'priority':
        return `${value}`;

      default:
        return typeof value === 'number' ? value.toLocaleString(i18n.language) : value;
    }
  };

  // Total visible span units - drives the grid template so footer aligns with header/rows
  const totalVisibleSpans = columns
    .filter(col => visibleColumns[col.key] !== false)
    .reduce((sum, col) => sum + (col.span || 0), 0);

  return (
    <div
      className="hidden sm:grid gap-3 px-3 py-1 bg-white/[0.02] border-t border-border text-[11px]"
      style={{ gridTemplateColumns: `repeat(${totalVisibleSpans}, minmax(0, 1fr))` }}
    >
      {columns
        .filter(col => visibleColumns[col.key] !== false)
        .map(col => (
          <div
            key={col.key}
            style={{ gridColumn: `span ${col.span}` }}
            className="flex items-center gap-1"
          >
            {AGGREGATABLE_COLUMNS.has(col.key) && (
              <AggregationDropdown
                value={footerConfig[col.key]?.function || 'none'}
                onChange={(key, fn) => onFooterConfigChange(key, fn)}
                columnKey={col.key}
                displayValue={
                  aggregations[col.key]
                    ? (col.key === 'title'
                        // a number, so the language's plural form is chosen ("1 task", "3 úlohy")
                        ? t('table_footer.tasks_count', { count: Number(aggregations[col.key].value) })
                        : formatResult(col.key, aggregations[col.key]))
                    : null
                }
              />
            )}
          </div>
        ))
      }
    </div>
  );
};

export default TableFooter;
